"use client";

import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc-client";
import type { CreatorPortalAccess } from "../creatorAuthContext";

/** The current time, re-rendering every `intervalMs`. */
export function useNow(intervalMs = 1000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** Every open request across the creator's programs, ranked by the server. */
export function useOpenRequests(access: CreatorPortalAccess | null) {
  const query = trpc.creatorPortal.getOpenRequests.useQuery(access ?? {}, {
    enabled: !!access,
    retry: false,
  });
  return query;
}

/** One open request; NOT_FOUND once it closes, expires or is out of reach. */
export function useOpenRequest(
  access: CreatorPortalAccess | null,
  id: string | null,
) {
  const query = trpc.creatorPortal.getOpenRequest.useQuery(
    { ...access, id: id ?? "" },
    { enabled: !!access && !!id, retry: false },
  );
  return query;
}

/** After a submission: the ranking and the requested request both moved. */
export function useInvalidateRequests() {
  const utils = trpc.useUtils();
  return () => {
    void utils.creatorPortal.getOpenRequests.invalidate();
    void utils.creatorPortal.getOpenRequest.invalidate();
  };
}
